import {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from 'components/Layout';
import AppShell from 'components/Layout/AppShell';
import Button from 'components/UI/Button';
import useLiterals from 'utils/hooks/useLiterals';
import { ROUTE_PATH } from 'routes';
import { fetchNotificationPage } from 'modules/notifications/actions';
import { NOTIFICATION_SEARCH_MAX } from 'modules/notifications/categories';
import { appendUnique } from 'modules/notifications/mapNotification';
import NotificationList from './components/NotificationList';
import NotificationFilters from './components/NotificationFilters';
import NotificationDetail from './components/NotificationDetail';

const EMPTY_FILTERS = { search: '', category: '' };

const normalizeFilters = (filters) => ({
  search: (filters.search || '').trim().slice(0, NOTIFICATION_SEARCH_MAX),
  category: filters.category || '',
});

const Notifications = () => {
  const literals = useLiterals('notifications');
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const detailId = params['*'] || '';

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [pending, setPending] = useState(EMPTY_FILTERS);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [last, setLast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [loadedOnce, setLoadedOnce] = useState(false);

  const requestSeq = useRef(0);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const lastRef = useRef(false);
  const pageRef = useRef(0);
  const appliedRef = useRef(EMPTY_FILTERS);
  const scrollTopRef = useRef(0);
  const restoreScrollRef = useRef(false);

  useEffect(() => {
    appliedRef.current = applied;
    pageRef.current = page;
    lastRef.current = last;
  }, [applied, page, last]);

  const showDetail = Boolean(detailId);
  const showFilters = filtersOpen && !showDetail;
  const showList = !showDetail && !showFilters;

  const loadPage = useCallback(async (nextPage, filters, mode) => {
    const replace = mode === 'replace';
    if (!replace) {
      if (loadingRef.current || loadingMoreRef.current || lastRef.current) {
        return;
      }
      loadingMoreRef.current = true;
      setLoadingMore(true);
      setError('');
    } else {
      loadingRef.current = true;
      loadingMoreRef.current = false;
      pageRef.current = 0;
      lastRef.current = false;
      setLoading(true);
      setLoadingMore(false);
      setError('');
      setItems([]);
      setPage(0);
      setLast(false);
    }

    // Descarta la respuesta si el usuario cambió filtros o la vista se desmontó.
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;

    try {
      const result = await fetchNotificationPage({
        page: nextPage,
        search: filters.search,
        category: filters.category,
      });
      if (requestSeq.current !== seq) return;
      const nextItems = result?.items || [];
      const nextNumber = Number.isInteger(result?.number) ? result.number : nextPage;
      const isLast = result?.last === true;
      setItems((current) => (
        replace ? appendUnique([], nextItems) : appendUnique(current, nextItems)
      ));
      setPage(nextNumber);
      setLast(isLast);
      pageRef.current = nextNumber;
      lastRef.current = isLast;
      setLoadedOnce(true);
    } catch (err) {
      if (requestSeq.current !== seq) return;
      if (err?.statusCode === 401) {
        setLoadedOnce(true);
        return;
      }
      setError(err?.statusCode === 403 ? 'forbidden' : 'loadError');
      setLoadedOnce(true);
      if (!replace) {
        lastRef.current = false;
      }
    } finally {
      if (requestSeq.current === seq) {
        loadingRef.current = false;
        loadingMoreRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => () => {
    requestSeq.current += 1;
  }, []);

  useEffect(() => {
    if (!showList || loadedOnce || loadingRef.current) return;
    loadPage(0, appliedRef.current, 'replace');
  }, [showList, loadedOnce, loadPage]);

  const loadMore = useCallback(() => {
    loadPage(pageRef.current + 1, appliedRef.current, 'append');
  }, [loadPage]);

  const openFilters = () => {
    setPending(applied);
    setFiltersOpen(true);
  };

  const closeFilters = () => {
    setPending(applied);
    setFiltersOpen(false);
  };

  const applyFilters = (nextFilters) => {
    const next = normalizeFilters(nextFilters);
    appliedRef.current = next;
    setApplied(next);
    setPending(next);
    setFiltersOpen(false);
    setLoadedOnce(true);
    scrollTopRef.current = 0;
    window.scrollTo(0, 0);
    loadPage(0, next, 'replace');
  };

  const openDetail = (id) => {
    scrollTopRef.current = window.scrollY || document.documentElement.scrollTop || 0;
    navigate(ROUTE_PATH.setNotification(id), {
      state: { fromNotifications: true },
    });
  };

  useLayoutEffect(() => {
    if (!detailId) return;
    window.scrollTo(0, 0);
  }, [detailId]);

  useLayoutEffect(() => {
    if (detailId || filtersOpen || !restoreScrollRef.current) return;
    restoreScrollRef.current = false;
    window.scrollTo(0, scrollTopRef.current || 0);
  }, [detailId, filtersOpen]);

  const handleBack = () => {
    if (showFilters) {
      closeFilters();
      return;
    }
    if (showDetail) {
      if (location.state?.fromNotifications) {
        restoreScrollRef.current = true;
        navigate(-1);
        return;
      }
      navigate(ROUTE_PATH.NOTIFICATIONS, { replace: true });
      return;
    }
    navigate(-1);
  };

  let title = literals.title;
  if (showFilters) title = literals.searchNotifications;

  return (
    <Layout title={title} onBack={handleBack} bottomMenu>
      <AppShell variant='wide'>
        {showList ? (
          <>
            <Button
              type='outline-primary'
              text={literals.searchNotifications}
              onClick={openFilters}
              className='mb-6'
              fullWidth
            />
            <NotificationList
              items={items}
              loading={loading || !loadedOnce}
              loadingMore={loadingMore}
              last={last}
              error={error}
              hasFilters={Boolean(applied.search || applied.category)}
              onOpen={openDetail}
              onLoadMore={loadMore}
            />
          </>
        ) : null}
        {showFilters ? (
          <NotificationFilters
            pending={pending}
            onSearchChange={(search) => setPending((current) => ({ ...current, search }))}
            onCategoryChange={(category) => setPending((current) => ({ ...current, category }))}
            onApply={() => applyFilters(pending)}
            onClear={() => applyFilters(EMPTY_FILTERS)}
          />
        ) : null}
        {showDetail ? <NotificationDetail id={detailId} /> : null}
      </AppShell>
    </Layout>
  );
};

export default Notifications;
